import React, {useLayoutEffect, useMemo} from 'react'
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import {
  useCollectionEntityAdd, useCollectionEntityAssign, useCollectionEntityDelete,
  useCollectionEntityGet,
  useCollectionEntityList, useCollectionEntityUnAssign
} from 'services/collection/entity/entity.hooks';
import {useParams} from 'react-router-dom';
import utils from 'helpers/utils';
import {useCollectionGet} from 'services/collection/collection.hooks';
import EntitiesTableProvider from 'components/organisms/Providers/TableProvider/EntitiesTableProvider';
import {useEffectEvent, useEffectItem, useProviderView} from 'helpers/hooks/utils';
import {
  useCollectionEntityColumns, useCollectionEntityFilterGroups, useCollectionEntityGraphs,
  useCollectionEntityPatch, useCollectionEntityCallbacks, useCollectionEntitySelected
} from 'services/collection/entity/entity.utils';
import {useEntityCsiPatch} from 'services/entity/csi/csi.utils';
import {useExportAdd} from 'services/export/export.hooks';
import {useEntityTaskCreate} from 'services/entity/comment/comment.utils';
import {useAuthClientId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useCollectionCustomFieldCache} from 'services/collection/customField/customField.hooks';
import {useQuestionnaireQuestions} from 'services/collection/questionnaire/questionnaire.utils';
import {useCollectionEntityAnswerAdd} from 'services/collection/entity/answer/answer.hooks';
import {useViewList} from 'services/view/view.hooks';

const CollectionEntitiesTableLoader = ({children, active}) => {
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState());

  const list = useCollectionEntityList({
    collectionId: tableProvider.context?.data?.collectionId,
    search: listState.search,
    filter: listState.filter,
    sort: listState.sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.infinite,
    enablePreload: true,
    enabled: (active && listState.active) && utils.isDefined(tableProvider.context?.data?.collectionId)
  });

  const setListEvent = useEffectEvent(tableProvider.setList);
  useLayoutEffect(() => {
    setListEvent?.(list);
  }, [setListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const CollectionEntitiesTableTestLoader = ({children, active}) => {
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState('test'));

  const list = useCollectionEntityList({
    collectionId: tableProvider.context?.data?.collectionId,
    search: listState.search,
    filter: listState.filter,
    sort: listState.sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.runOnce,
    enabled: (active && listState.active) && +tableProvider.context?.data?.collectionId > 0
  });

  const setTestListEvent = useEffectEvent(tableProvider.setTestList);
  useLayoutEffect(() => {
    setTestListEvent?.(list);
  }, [setTestListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const CollectionEntitiesPreLoader = ({active, id}) => {
  const tableProvider = useTable();

  useCollectionEntityGet({
    collectionId: tableProvider.context?.data?.collectionId,
    entityId: id
  },
  {enabled: (active && +tableProvider.context?.data?.collectionId > 0 && +id > 0)});
}

const CollectionEntitiesTableProvider = (props) => {
  const params = useParams();
  const { collectionId, entityId } = {...params, ...props};

  const clientId = useAuthClientId();

  const collection = useCollectionGet({collectionId}, {enabled: collectionId > 0});
  const entity = useCollectionEntityGet({collectionId, entityId},
    {enabled: (+collectionId > 0 && +entityId > 0)});

  const customFields = useCollectionCustomFieldCache({collectionId: collection.data?.collectionId},
    {enabled: clientId >= 0 && collection.data?.collectionId > 0});
  const customFieldsCache = (customFields.data?.data ?? customFields.data);
  const [questionnaireQuestions, isLoadingQuestions, isSuccessQuestions] = useQuestionnaireQuestions(collection?.data);

  const views = useViewList({
    relationType: 'collection',
    relationId: collectionId,
    subscribed: true,
    allPages: true
  }, {
    enabled: collectionId > 0
  });
  const view = useProviderView('collection.entity', views.data, props.skipView);

  const columns = useCollectionEntityColumns(view, collection?.data, customFieldsCache, questionnaireQuestions);
  const filterGroups = useCollectionEntityFilterGroups(view, collection?.data, customFieldsCache);
  const graphs = useCollectionEntityGraphs(view);

  // isLoading anything, is loading something definition related
  const isLoading = props?.isLoading || customFields.status?.isLoading || collection.status?.isLoading || views.status?.isLoading || isLoadingQuestions;
  const isDefinitionsLoading = props?.isDefinitionsLoading || !customFields.status?.isSuccess || !collection.status?.isSuccess || !isSuccessQuestions;

  const entityProfileCallbacks = useCollectionEntityCallbacks(collection.data);

  const columnDefinitions = useMemo(() => {
    return columns?.length > 0 ? columns : null;
  }, [columns]);

  const filterGroupDefinitions = useMemo(() => {
    return filterGroups?.length > 0 ? filterGroups : null;
  }, [filterGroups]);

  const graphDefinitions = useMemo(() => {
    return graphs?.length > 0 ? graphs : null;
  }, [graphs]);

  const options = useMemo(() => ({
    listState: {
      initial: {
        pageSize: 25
      },
      options: {
        type: constants.appState.type.local,
        name: `collectionEntities_${collectionId}`,
        searchParams: true
      }
    },
    testListState: {
      initial: {
        active: false,
        pageSize: 0
      },
      options: {
        name: `collectionEntities_${collectionId}_test`
      }
    },
    listSelection: {
      options: {
        max: constants.selection.max.collection
      }
    },
    columnState: {
      initial: {},
      options: {
        name: `collectionEntities_${collectionId}`,
        type: constants.appState.type.local
      }
    },
    kanbanState: {
      initial: {},
      options: {
        name: `collectionEntities_${collectionId}`,
        type: constants.appState.type.local
      }
    },
    graphState: {
      initial: {},
      options: {
        name: `collectionEntities_${collectionId}`,
        type: constants.appState.type.local
      }
    },
    assistantState: {
      initial: {},
      options: {
        name: `collectionEntities_${collectionId}`,
        type: constants.appState.type.local
      }
    },
    columnDefinitions,
    filterGroupDefinitions,
    graphDefinitions
  }), [columnDefinitions, filterGroupDefinitions, graphDefinitions, collectionId]);

  const fieldData = useMemo(() => {
    return {
      customFields: customFieldsCache,
      tagGroups: collection.data?.tagGroups,
      questionnaireQuestions: questionnaireQuestions,
      callbacks: entityProfileCallbacks
    };
  }, [collection.data?.tagGroups, questionnaireQuestions, customFieldsCache, entityProfileCallbacks]);

  const update = useCollectionEntityPatch();
  const create = useCollectionEntityAdd();
  const deleteData = useCollectionEntityDelete();
  const assignItems = useCollectionEntityAssign();
  const unAssignItems = useCollectionEntityUnAssign();
  const acceptItems = useCollectionEntityAssign({deleteItem: true});

  const createTask = useEntityTaskCreate();
  const createAnswer = useCollectionEntityAnswerAdd();
  const downloadItems = useExportAdd();
  const selectedItems = useCollectionEntitySelected(collectionId);
  const updateCsi = useEntityCsiPatch();
  const updaters = useMemo(() => {
    return {
      createData: (data) => {
        return create.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      updateData: (entity, changes) => {
        return update(collection.data, entity, changes);
      },
      deleteData: (data) => {
        return deleteData.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      assignItems: (data) => {
        return assignItems.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      unAssignItems: (data) => {
        return unAssignItems.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      acceptItems: (data) => {
        return acceptItems.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      createAnswer: (data) => {
        return createAnswer.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      createTask: (entities, data) => {
        return createTask(entities, collection.data?.collectionId, data);
      },
      updateCsi: (entity, changes) => {
        return updateCsi(entity, {collectionId: collection.data?.collectionId, ...changes});
      },
      downloadItems: (data) => {
        return downloadItems.mutation.mutateAsync({collectionId: collection.data?.collectionId, ...data});
      },
      selectedItems: selectedItems
    }
  }, [update, create.mutation, deleteData.mutation, assignItems.mutation,
    unAssignItems.mutation, downloadItems.mutation, acceptItems.mutation,
    createAnswer.mutation, updateCsi, createTask, selectedItems, collection.data]);

  const defaults = useMemo(() => {
    return utils.mergeObjects({
      list: {
        filter: [{id: 'scope', value: 'inside'}]
      }
    }, props.defaults);
  }, [props.defaults]);

  return <EntitiesTableProvider {...props}
                                view={view}
                                options={options}
                                fieldData={fieldData}
                                context={collection}
                                data={entity}
                                updaters={updaters}
                                defaults={defaults}
                                views={views}
                                LoaderComponent={CollectionEntitiesTableLoader}
                                TestLoaderComponent={CollectionEntitiesTableTestLoader}
                                PreLoaderComponent={CollectionEntitiesPreLoader}
                                isDefinitionsLoading={isDefinitionsLoading}
                                isLoading={isLoading}>
    {props.children}
  </EntitiesTableProvider>
};

CollectionEntitiesTableProvider.propTypes = {
}

export default CollectionEntitiesTableProvider;
